import { useContext, useEffect, useState } from "react";
import defaultPic from "../../../../asset/image/default_photo.png";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import { SETTING_ITEMS } from "../../../../helperData/ApiAddress";
import { collectTreeIds } from "../../../../component/global/DBSearchableTree/treeUtils";
import { t } from "i18next";
import "./styles.scss";
import DBSpinner from "../../../../component/global/DBSpinner";

import DBTable from "../../../../component/global/DBTable/DBTable";
import {
    Add,
    Edit,
    Import,
    ImportCurve,
    More,
    Printer,
    SearchNormal1,
    Trash,
} from "iconsax-react";
import DBPagination from "../../../../component/global/DBPagination";
import DBAddButton from "../../../../component/global/DBAddButton";
import DBInput from "../../../../component/global/DBInput";
import DBConfirmModal from "./../../../../component/global/DBConfirmModal/index";
import { ToastContext } from "../../../../component/global/ToastContext";
import ItemsModal from "./ItemsModal";
import GroupAddEditModal from "./GroupAddEditModal";
import PrintPosterDownloadModal from "./PrintPosterDownloadModal";
import DBButton from "../../../../component/global/DBButton";

const PAGE_SIZE = 20;

export default function ItemsTab({ itemtype }) {
    const toast = useContext(ToastContext);

    const [getItems, getItemsLoader, cancelGetItem] = useRequest(
        SETTING_ITEMS,
        "GET",
        undefined,
        undefined,
        { "accept-language": "*" }
    );
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [featuresModalShow, setFeaturesModalShow] = useState(false);
    const [groupAddEditModalShow, setGroupAddEditModalShow] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [searchStr, setSearchStr] = useState("");
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
    const [printItem, setPrintItem] = useState(null);
    const [printAll, setPrintAll] = useState(false);

    const [deleteItems, deleteItemsLoader] = useRequest(
        SETTING_ITEMS + deleteConfirmModal,
        "DELETE"
    );

    const getItemsHandle = (page = currentPage) => {
        if (!itemtype) return;
        getItems({
            item_type_in: collectTreeIds([itemtype]).join(","),
            page,
            name: searchStr,
        }).then((r) => {
            setItems(Array.isArray(r?.results) ? r.results : []);
            setTotalCount(Number(r?.count) || 0);
        });
    };

    const searchItems = () => {
        if (currentPage === 1) {
            getItemsHandle(1);
            return;
        }
        setCurrentPage(1);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [itemtype?.id]);

    useEffect(() => {
        if (!itemtype) {
            setItems([]);
            setTotalCount(0);
            return;
        }
        cancelGetItem?.();
        getItemsHandle();
    }, [itemtype, currentPage]);

    const headers = [
        {
            id: "row",
            title: t("item.row"),
            gridTemplateColumns: "60px",
        },
        {
            id: "name",
            title: t("item.name"),
            gridTemplateColumns: "3fr",
        },
        {
            id: "photo",
            title: t("item.photo"),
            gridTemplateColumns: "105px",
        },
        {
            id: "parent",
            title: t("item.parent"),
            gridTemplateColumns: "2fr",
        },
        {
            id: "options",
            title: t("item.options"),
            gridTemplateColumns: "168px",
        },
    ];

    const rows = items?.map((item, index) => {
        return {
            row: (currentPage - 1) * PAGE_SIZE + index + 1,
            name: item.name,
            photo: (
                <div className="item-photo">
                    <img
                        src={item.photo || defaultPic}
                        alt="item photo"
                        onErrorCapture={(e) => (e.target.src = defaultPic)}
                    />
                </div>
            ),
            parent: item.parent?.name || t("item.no.parent"),
            options: (
                <div className="option-buttons">
                    <button
                        type="button"
                        className="option-icon-button"
                        title={t("print.poster.download.title")}
                        aria-label={t("print.poster.download.title")}
                        onClick={() => setPrintItem(item)}
                    >
                        <Printer size="18" color="#2e71fc" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="option-icon-button"
                        title={t("item.open.feedback")}
                        aria-label={t("item.open.feedback")}
                        onClick={() => goToPreviewHandle(item.qr_code)}
                    >
                        <More size="18" color="#2e71fc" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="option-icon-button"
                        title={t("create.item")}
                        aria-label={t("create.item")}
                        onClick={() => {
                            setEditMode(item.id);
                            setFeaturesModalShow(true);
                        }}
                    >
                        <Edit size="18" color="#2e71fc" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="option-icon-button option-icon-button--danger"
                        title={t("item.delete")}
                        aria-label={t("item.delete")}
                        onClick={() => setDeleteConfirmModal(item.id)}
                    >
                        <Trash size="18" color="red" aria-hidden="true" />
                    </button>
                </div>
            ),
        };
    });

    const deleteItemsHandle = () => {
        deleteItems()
            .then(() => {
                toast(t("item.deleted"), "success");
                setDeleteConfirmModal(false);
                getItemsHandle();
            })
            .catch((e) => {
                toast(t("item.delete.error"), "danger");
            });
    };

    const getPublicWebsiteBase = () => {
        const configured = (process.env.REACT_APP_WEBSITE || "").replace(
            /\/$/,
            ""
        );
        if (!configured) {
            return window.location.origin;
        }
        try {
            const configuredHost = new URL(configured).hostname;
            if (configuredHost !== window.location.hostname) {
                return window.location.origin;
            }
        } catch (_) {
            return window.location.origin;
        }
        return configured;
    };

    const goToPreviewHandle = (qrCode) => {
        window.open(
            `${getPublicWebsiteBase()}${process.env.PUBLIC_URL}/feedback/${encodeURIComponent(qrCode)}`
        );
    };

    return (
        <div className="items-tab">
            <DBConfirmModal
                show={deleteConfirmModal}
                onHide={() => setDeleteConfirmModal(false)}
                btnTitle={t("item.delete")}
                onConfirm={deleteItemsHandle}
                title={t("item.delete")}
                description={t("item.delete.message")}
                loading={deleteItemsLoader}
            />
            <ItemsModal
                showModal={featuresModalShow}
                onHide={() => setFeaturesModalShow(false)}
                itemType={itemtype}
                onSuccess={getItemsHandle}
                editMode={editMode}
            />
            <GroupAddEditModal
                show={groupAddEditModalShow}
                onHide={() => setGroupAddEditModalShow(false)}
                itemType={itemtype}
                onSuccess={getItemsHandle}
            />
            <PrintPosterDownloadModal
                show={Boolean(printItem) || printAll}
                item={printItem}
                bulk={printAll}
                itemType={itemtype}
                searchName={searchStr}
                onHide={() => {
                    setPrintItem(null);
                    setPrintAll(false);
                }}
            />
            <div className="items-toolbar">
                <div>
                    <DBInput
                        size="large"
                        placeholder={t("item.search")}
                        type="search"
                        allowClear
                        disabled={!itemtype}
                        onChange={(e) => {
                            setSearchStr(e.target.value);
                        }}
                        onPressEnter={searchItems}
                    />
                    <DBAddButton
                        icon={<SearchNormal1 size={18} color="#2e71fc" />}
                        disabled={!itemtype}
                        onClick={searchItems}
                    />
                </div>
                <div className="button-section">
                    <DBButton
                        caption={
                            <>
                                <Printer size={18} aria-hidden="true" />
                                <span>{t("print.poster.download.all")}</span>
                            </>
                        }
                        outlined
                        disabled={!itemtype}
                        title={t("print.poster.download.all")}
                        aria-label={t("print.poster.download.all")}
                        onClick={() => {
                            setPrintItem(null);
                            setPrintAll(true);
                        }}
                    />
                    <DBButton
                        caption={
                            <>
                                <Add />
                                <span>{t("settings.add")}</span>
                            </>
                        }
                        outlined
                        disabled={!itemtype}
                        onClick={() => {
                            setEditMode(false);
                            setFeaturesModalShow(true);
                        }}
                    />
                    <DBAddButton
                        icon={<ImportCurve size={18} color="#2e71fc" />}
                        disabled={!itemtype}
                        onClick={() => setGroupAddEditModalShow(true)}
                    />
                </div>
            </div>
            <div className="items-table">
                {!itemtype ? (
                    <div className="not-itemtype">
                        {t("items.not.itemtype")}
                    </div>
                ) : getItemsLoader ? (
                    <DBSpinner tip={t("loading")} />
                ) : items.length === 0 ? (
                    <div className="not-itemtype">{t("items.empty")}</div>
                ) : (
                    <DBTable minWidth={700} headers={headers} rows={rows} />
                )}
            </div>
            <div className="items-pagination">
                <DBPagination
                    total={totalCount}
                    pageSize={PAGE_SIZE}
                    onChange={(page) => {
                        setCurrentPage(page);
                    }}
                    current={currentPage}
                />
            </div>
        </div>
    );
}
